"use client";

import { useEffect, useState } from "react";
import { useWebMcpTool } from "@/lib/webmcp/react";
import { isWebMcpVisible } from "@/lib/webmcp/visibility";
import { z } from "zod";

const requestInput = z.object({}).strict();

export default function WebMcpLeadMagnet({ slug }: { slug: string }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        const link = document.getElementById(`webmcp-lead-magnet-request-${slug}`);
        const nextVisible = link instanceof HTMLAnchorElement
          && link.href.startsWith("mailto:")
          && isWebMcpVisible(link);
        setVisible((currentVisible) => currentVisible === nextVisible ? currentVisible : nextVisible);
      }, 80);
    };
    const observer = new MutationObserver(refresh);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["href", "hidden", "aria-hidden", "class", "style", "open"],
    });
    window.addEventListener("resize", refresh);
    window.visualViewport?.addEventListener("resize", refresh);
    refresh();

    return () => {
      if (timeout) clearTimeout(timeout);
      observer.disconnect();
      window.removeEventListener("resize", refresh);
      window.visualViewport?.removeEventListener("resize", refresh);
    };
  }, [slug]);

  useWebMcpTool({
    action: "request_lead_magnet",
    instance: slug.replaceAll("-", "_"),
    schema: requestInput,
    enabled: visible,
    execute: () => {
      const link = document.getElementById(`webmcp-lead-magnet-request-${slug}`);
      if (!(link instanceof HTMLAnchorElement) || !link.href.startsWith("mailto:") || !isWebMcpVisible(link)) {
        return { status: "unavailable" as const };
      }

      link.focus();
      return { status: "review_required" as const, uiState: { focused: true } };
    },
  });

  return null;
}
